import React, { useEffect, useState, ChangeEvent, FormEvent } from 'react';
import {
    ref,
    onValue,
    update,
    remove,
    push,
    set,
    getDatabase,
    child,
} from 'firebase/database';
import Modal from 'react-modal';
import COLORS, {useBuildingKey} from "../../Helpers/utils";
import {Helmet} from "react-helmet-async";
import {BreadcrumbComponent} from "../../Components/BreadCrumb";
import {ContactModel} from "../../types/protocols";
import {useBuilding} from "../../Helpers/BuildingContext";

Modal.setAppElement('#root');

const modalStyles: Modal.Styles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
        width: '600px',
        maxHeight: '80vh',
        overflowY: 'auto',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
        border: 'none',
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
};

// Page container
const containerStyle: React.CSSProperties = {
    background: '#f8f9fa',
    minHeight: '100vh',
    padding: '30px',
};

// Header
const headerStyle: React.CSSProperties = {
    textAlign: 'center',
    marginBottom: '30px',
};

// Cards list
const listStyle: React.CSSProperties = {
    listStyleType: 'none',
    padding: 0,
    maxWidth: '600px',
    margin: '0 auto 30px',
};

// Individual contact card
const cardStyle: React.CSSProperties = {
    backgroundColor: '#ffffff',
    padding: '15px',
    marginBottom: '10px',
    borderRadius: '8px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
};

// Contact info container
const infoContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left'
};

// Contact name
const nameStyle: React.CSSProperties = {
    fontSize: '1.2rem',
    fontWeight: 600,
    marginBottom: '5px',
};

// Base button style
const baseButtonStyle: React.CSSProperties = {
    marginRight: '10px',
    padding: '8px 14px',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
};

// Specific button variants
const editButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    backgroundColor: COLORS.secondary,
    marginRight: 0,
};

const manageButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    backgroundColor: COLORS.primaryDark,
    marginRight: 'auto',
    marginLeft: 'auto',
    marginBottom: '30px',
    display: 'block',
};

const cancelButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    backgroundColor: COLORS.secondary,
};

const confirmButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    backgroundColor: COLORS.primaryDark,
    marginRight: 0,
};

const doneButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    backgroundColor: COLORS.primaryDark,
    marginRight: 'auto',
    marginLeft: 'auto',
    marginBottom: '20px',
    display: 'block',
};

const addContactButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    backgroundColor: '#28a745',
    marginBottom: '20px',
};

const deleteButtonStyle: React.CSSProperties = {
    ...baseButtonStyle,
    backgroundColor: COLORS.redOrange,
    marginLeft: '10px',
};

// Input and error styles
const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px',
    marginTop: '5px',
    boxSizing: 'border-box',
    borderRadius: '4px',
    border: '1px solid #ccc',
};

const errorStyle: React.CSSProperties = {
    border: '1px solid red',
};

// Form label and field wrapper
const fieldWrapperStyle: React.CSSProperties = {
    marginBottom: '10px',
    display: 'flex',
    flexDirection: 'column',
};

// Checkbox row style
const checkboxRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: '10px',
    marginBottom: '5px',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    border: '1px solid #e9ecef',
};

const checkboxStyle: React.CSSProperties = {
    marginRight: '15px',
    transform: 'scale(1.2)',
};

// An array to auto-generate fields (excluding 'name' which is handled separately)
const contactFields = [
    { label: 'Roll', name: 'role', type: 'text' },
    { label: 'Företag', name: 'company', type: 'text' },
    { label: 'Telefonnummer', name: 'phoneNumber', type: 'text' },
    { label: 'E-post', name: 'email', type: 'email' },
];

const Contacts: React.FC = () => {
    // All contacts from the central registry
    const [allContacts, setAllContacts] = useState<{ [key: string]: ContactModel }>({});

    // Contact keys that are assigned to this building
    const [buildingContactKeys, setBuildingContactKeys] = useState<string[]>([]);

    // Modal states
    const [isRegistryModalOpen, setIsRegistryModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    // Contact management states
    const [selectedContactKeys, setSelectedContactKeys] = useState<string[]>([]);
    const [contactToDelete, setContactToDelete] = useState<string | null>(null);
    const [isEditingContact, setIsEditingContact] = useState(false);
    const [currentContact, setCurrentContact] = useState<ContactModel | null>(null);

    // Form states
    const [formData, setFormData] = useState<ContactModel>({
        key: '',
        name: '',
        role: '',
        company: '',
        phoneNumber: '',
        email: '',
        info: '',
    });
    const [formErrors, setFormErrors] = useState<{ name?: string }>({});

    const database = getDatabase();
    const buildingKey = useBuildingKey();
    const contactRegistryRef = ref(database, 'contactRegistry');
    const buildingContactsRef = ref(database, `buildings/${buildingKey}/contactKeys`);
    const { currentBuilding } = useBuilding();

    // Subscribe to all contacts in the registry
    useEffect(() => {
        const unsubscribe = onValue(contactRegistryRef, snapshot => {
            const data = snapshot.val();
            setAllContacts(data || {});
        });
        return () => unsubscribe();
    }, [database]);

    // Subscribe to building's contact keys
    useEffect(() => {
        const unsubscribe = onValue(buildingContactsRef, snapshot => {
            const data = snapshot.val();
            setBuildingContactKeys(data || []);
        });
        return () => unsubscribe();
    }, [database]);

    // Get contacts that are assigned to this building and clean up orphaned keys
    const validContactKeys = buildingContactKeys.filter(key => allContacts[key]);
    const buildingContacts = validContactKeys
        .map(key => allContacts[key])
        .reduce((acc, contact) => {
            acc[contact.key] = contact;
            return acc;
        }, {} as { [key: string]: ContactModel });

    // Clean up orphaned keys if any were found
    useEffect(() => {
        if (buildingContactKeys.length > 0 && validContactKeys.length !== buildingContactKeys.length) {
            console.log(`Cleaning up ${buildingContactKeys.length - validContactKeys.length} orphaned contact references`);
            set(buildingContactsRef, validContactKeys);
        }
    }, [buildingContactKeys, validContactKeys, buildingContactsRef]);

    const openRegistryModal = () => {
        setSelectedContactKeys([...buildingContactKeys]);
        setIsRegistryModalOpen(true);
    };

    const closeRegistryModal = () => {
        setIsRegistryModalOpen(false);
        setSelectedContactKeys([]);
    };

    const openCreateModal = (contact?: ContactModel) => {
        if (contact) {
            // Edit mode
            setIsEditingContact(true);
            setCurrentContact(contact);
            setFormData(contact);
        } else {
            // Create mode
            setIsEditingContact(false);
            setCurrentContact(null);
            setFormData({
                key: '',
                name: '',
                role: '',
                company: '',
                phoneNumber: '',
                email: '',
                info: '',
            });
        }
        setFormErrors({});
        setIsCreateModalOpen(true);
    };

    const closeCreateModal = () => {
        setIsCreateModalOpen(false);
        setIsEditingContact(false);
        setCurrentContact(null);
    };

    const openDeleteModal = (key: string) => {
        setContactToDelete(key);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setContactToDelete(null);
        setIsDeleteModalOpen(false);
    };

    const handleCheckboxChange = (contactKey: string, checked: boolean) => {
        if (checked) {
            setSelectedContactKeys(prev => [...prev, contactKey]);
        } else {
            setSelectedContactKeys(prev => prev.filter(key => key !== contactKey));
        }
    };

    const handleSaveBuildingContacts = () => {
        set(buildingContactsRef, selectedContactKeys).then(() => {
            closeRegistryModal();
        });
    };

    const handleFormChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear name error if user enters a value
        if (name === 'name' && value.trim() !== '') {
            setFormErrors(prev => ({ ...prev, name: undefined }));
        }
    };

    const handleSaveContact = (e: FormEvent) => {
        e.preventDefault();

        // Validate required fields
        if (!formData.name.trim()) {
            setFormErrors({ name: 'Name is required.' });
            return;
        }

        if (isEditingContact && currentContact?.key) {
            // Edit existing contact
            const contactRef = child(contactRegistryRef, currentContact.key);
            update(contactRef, {
                name: formData.name,
                role: formData.role,
                company: formData.company,
                phoneNumber: formData.phoneNumber,
                email: formData.email,
                info: formData.info,
            }).then();
        } else {
            // Create new contact
            const newContactRef = push(contactRegistryRef);
            const contactData = { ...formData, key: newContactRef.key };
            set(newContactRef, contactData).then(() => {
                // Auto-assign new contact to current building
                if (newContactRef.key) {
                    const updatedKeys = [...buildingContactKeys, newContactRef.key];
                    set(buildingContactsRef, updatedKeys);
                }
            });
        }

        closeCreateModal();
    };

    const handleDeleteContact = () => {
        if (contactToDelete) {
            // Remove from registry
            const contactRef = child(contactRegistryRef, contactToDelete);
            remove(contactRef).then(() => {
                // The orphaned key cleanup will happen automatically via the useEffect
                // when the contact is removed from the registry and allContacts updates
            });

            closeDeleteModal();
        }
    };

    return (
        <>
            <BreadcrumbComponent
                items={[
                    {text: currentBuilding?.summary.name ?? "Byggnad", link: `/building/${buildingKey}`},
                    {text: "Kontakter"}
                ]}
            />
            <div style={containerStyle}>
                <Helmet>
                    <title>Kontakter</title>
                </Helmet>
                <h1 style={headerStyle}>Kontakter</h1>

                {/* Display building contacts */}
                <ul style={listStyle}>
                    {Object.keys(buildingContacts).map(key => {
                        const contact = buildingContacts[key];
                        return (
                            <li key={key} style={cardStyle}>
                                <div style={infoContainerStyle}>
                                    <span style={nameStyle}>{contact.name}</span>
                                    {contact.role && <div>Roll: {contact.role}</div>}
                                    {contact.company && <div>Företag: {contact.company}</div>}
                                    {contact.phoneNumber && <div>Telefonnummer: {contact.phoneNumber}</div>}
                                    {contact.email && <div>E-post: {contact.email}</div>}
                                    {contact.info && <div>Info: {contact.info}</div>}
                                </div>
                                <div>
                                    <button style={editButtonStyle} onClick={() => openCreateModal(contact)}>
                                        Redigera
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>

                <button onClick={openRegistryModal} style={manageButtonStyle}>
                    Hantera kontakter
                </button>

                {/* Contact Registry Modal */}
                <Modal
                    isOpen={isRegistryModalOpen}
                    onRequestClose={closeRegistryModal}
                    contentLabel="Hantera kontakter"
                    style={modalStyles}
                >
                    <h2>Kontaktregister</h2>

                    {
                        Object.values(allContacts).length === 0 ?
                            <p>Det finns ännu inga kontakter i registret.</p>
                            :
                            <p>Markera vilka kontakter som är relevanta för denna byggnad</p>
                    }

                    <button onClick={() => openCreateModal()} style={addContactButtonStyle}>
                        Lägg till ny kontakt
                    </button>

                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {Object.keys(allContacts).map(contactKey => {
                            const contact = allContacts[contactKey];
                            const isSelected = selectedContactKeys.includes(contactKey);

                            return (
                                <div key={contactKey} style={checkboxRowStyle}>
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={(e) => handleCheckboxChange(contactKey, e.target.checked)}
                                        style={checkboxStyle}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                                            {contact.name}
                                        </div>
                                        {contact.role && <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                            {contact.role}
                                        </div>}
                                        {contact.company && <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                            {contact.company}
                                        </div>}
                                    </div>
                                    <div>
                                        <button
                                            onClick={() => openCreateModal(contact)}
                                            style={{ ...baseButtonStyle, backgroundColor: COLORS.secondary, marginRight: '5px' }}
                                        >
                                            Redigera
                                        </button>
                                        <button
                                            onClick={() => openDeleteModal(contactKey)}
                                            style={deleteButtonStyle}
                                        >
                                            Radera
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                        <button onClick={handleSaveBuildingContacts} style={doneButtonStyle}>
                            Klar
                        </button>
                    </div>
                </Modal>

                {/* Create/Edit Contact Modal */}
                <Modal
                    isOpen={isCreateModalOpen}
                    onRequestClose={closeCreateModal}
                    contentLabel={isEditingContact ? 'Redigera kontakt' : 'Lägg till kontakt'}
                    style={modalStyles}
                >
                    <h2>{isEditingContact ? 'Redigera kontakt' : 'Lägg till kontakt'}</h2>
                    <form onSubmit={handleSaveContact}>
                        {/* Name Field (required) */}
                        <div style={fieldWrapperStyle}>
                            <label>Namn *</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleFormChange}
                                style={{
                                    ...inputStyle,
                                    ...(formErrors.name ? errorStyle : {}),
                                }}
                                required
                            />
                            {formErrors.name && (
                                <div style={{ color: 'red', marginTop: '5px' }}>{formErrors.name}</div>
                            )}
                        </div>

                        {contactFields.map(field => (
                            <div style={fieldWrapperStyle} key={field.name}>
                                <label>{field.label}</label>
                                <input
                                    type={field.type}
                                    name={field.name}
                                    value={formData[field.name as keyof ContactModel] as string}
                                    onChange={handleFormChange}
                                    style={inputStyle}
                                />
                            </div>
                        ))}

                        <div style={fieldWrapperStyle}>
                            <label>Info</label>
                            <textarea
                                name="info"
                                value={formData.info}
                                onChange={handleFormChange}
                                style={inputStyle}
                            />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                            <button type="button" onClick={closeCreateModal} style={cancelButtonStyle}>
                                Avbryt
                            </button>
                            <button type="submit" style={confirmButtonStyle}>
                                {isEditingContact ? 'Spara ändringar' : 'Lägg till'}
                            </button>
                        </div>
                    </form>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal
                    isOpen={isDeleteModalOpen}
                    onRequestClose={closeDeleteModal}
                    contentLabel="Bekräfta radera"
                    style={modalStyles}
                >
                    <h2>Bekräfta radera</h2>
                    <p>Är du säker på att du vill radera denna kontakt? Den kommer att tas bort från alla byggnader.</p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                        <button onClick={closeDeleteModal} style={cancelButtonStyle}>
                            Avbryt
                        </button>
                        <button onClick={handleDeleteContact} style={deleteButtonStyle}>
                            Radera
                        </button>
                    </div>
                </Modal>
            </div>
        </>
    );
};

export default Contacts;